// Global
import {
  Field,
  GetStaticComponentProps,
  Text,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useTheme } from 'lib/context/ThemeContext';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { Item } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

// Components
import { ComponentProps } from 'lib/types/component-props';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { normalizeGuid } from 'lib/utils/string-utils';
import React, { useEffect, useRef, useState } from 'react';
import Component from 'src/helpers/Component/Component';
import Headline from 'src/helpers/Headline/Headline';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import { Tabs } from 'src/helpers/Tabs';
import ToggleSwitch from 'src/helpers/ToggleSwitch/ToggleSwitch';
import Promo from './Promo.helper';
import { TabsFeaturedPromoTheme } from './TabsFeaturedPromo.theme';

type TabItemsProps = {
  fields: {
    children: Array<Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo>;
    headlineLevel: Item;
    headlineText: Field<string>;
  };
};

type TabCollectionsProps = {
  fields: {
    children: Array<
      Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo &
        TabItemsProps
    >;
  };
};

export type TabsFeaturedPromoProps =
  Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo &
    TabCollectionsProps &
    ComponentProps;

const TabsFeaturedPromo = (props: TabsFeaturedPromoProps) => {
  const { fields } = props;
  const { children } = fields || {};

  const { themeName, themeData } = useTheme(TabsFeaturedPromoTheme);
  const { currentScreenWidth } = useCurrentScreenType();

  const titlesScroller = useRef<HTMLDivElement>(null);

  //#region initial states and data of tabs

  // ToggleSwitch
  const [activeSwitchIndex, setActiveSwitchIndex] = useState(0);
  //Sets visible tab group
  const [visiblePanelIndex, setVisiblePanelIndex] = useState(0);
  //Sets visible tab panel
  const [currentIndex, setCurrentIndex] = useState(-1);
  // Sets if hash should be updated
  const [shouldUpdateURL, setShouldUpdateURL] = useState(false);

  //#endregion

  useEffect(() => {
    if (!children || children.length === 0) {
      return;
    }

    const selectedId = window.location.hash?.slice(1);
    const defaultTabId = (fields.defaultActiveTab as any)?.fields?.contentId?.value;

    let selected = false;

    if (selectedId) {
      for (let collectionIndex = 0; collectionIndex < children.length; collectionIndex++) {
        const items = children[collectionIndex].fields.children;
        for (let itemIndex = 0; itemIndex < items?.length; itemIndex++) {
          const item = items[itemIndex];
          const idToCompare = item.fields.contentId.value || `tab-${normalizeGuid(item.id)}`;
          if (selectedId === idToCompare) {
            setActiveSwitchIndex(collectionIndex);
            setVisiblePanelIndex(collectionIndex);
            setCurrentIndex(itemIndex);
            selected = true;
            break;
          }
        }
        if (selected) {
          break;
        }
      }
    }

    if (!selected && defaultTabId) {
      for (let collectionIndex = 0; collectionIndex < children.length; collectionIndex++) {
        const items = children[collectionIndex].fields.children;
        for (let itemIndex = 0; itemIndex < items?.length; itemIndex++) {
          const item = items[itemIndex];
          if (item.fields.contentId.value === defaultTabId) {
            setActiveSwitchIndex(collectionIndex);
            setVisiblePanelIndex(collectionIndex);
            setCurrentIndex(itemIndex);
            selected = true;
            break;
          }
        }
        if (selected) {
          break;
        }
      }
    }

    if (!selected && children?.[0]?.fields?.children?.[0]) {
      setActiveSwitchIndex(0);
      setVisiblePanelIndex(0);
      setCurrentIndex(0);
    }
  }, [children, fields?.defaultActiveTab]);

  // get the tab title appear into view if the tabs are in scrollable section
  useEffect(() => {
    const selectedItem = (fields?.children?.[visiblePanelIndex]?.fields.children ?? [])[
      currentIndex
    ];
    const selectedItemId =
      selectedItem?.fields?.contentId.value || `tab-${normalizeGuid(selectedItem?.id)}`;

    if (shouldUpdateURL && currentIndex >= 0) {
      history.pushState(history.state, window.location.href, `#${selectedItemId}`);
      setShouldUpdateURL(false);

      // Defer scroll until after DOM updates
      setTimeout(() => {
        const tabElement = document.getElementById(selectedItemId);
        if (tabElement) {
          tabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 0);
    }

    if (
      titlesScroller.current &&
      currentScreenWidth <= getBreakpoint('ml') &&
      selectedItemId.replace('tab-', '').length > 0
    ) {
      // We're using ref of the parent node of the titles in order to avoid bugs which can occur
      // if this component is authored multiple times in single page
      titlesScroller.current
        .querySelector(`[id='tab-${selectedItemId}']`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    // "fields.children" can be ignored as it is coming from layout service. We can ignore this warning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePanelIndex, currentIndex, shouldUpdateURL, currentScreenWidth]);

  const getSwitchTitles = (): string[] => {
    const switchTitles = ['', ''];

    children.forEach(
      (
        collection: Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo,
        index: number
      ) => {
        switchTitles[index] = (collection?.fields?.mobileToggleTitle as Field<string>).value;
      }
    );

    return switchTitles;
  };

  const selectTab = (itemIndex: number, collectionIndex: number) => {
    setShouldUpdateURL(true);
    setCurrentIndex(itemIndex);
    setVisiblePanelIndex(collectionIndex);
  };

  const renderToggleSwitch = () => {
    if (currentScreenWidth <= getBreakpoint('ml')) {
      return (
        <ToggleSwitch
          switchTitles={getSwitchTitles()}
          activeSwitchIndex={activeSwitchIndex}
          toggleActiveSwtich={() => {
            const _activeSwitchIndex = activeSwitchIndex === 0 ? 1 : 0;
            setVisiblePanelIndex(_activeSwitchIndex);
            setActiveSwitchIndex(_activeSwitchIndex);
            setCurrentIndex(0);
          }}
        />
      );
    }
    return null;
  };
  const tabRefs = useRef<Array<HTMLLIElement | null>>([]);
  tabRefs.current = [];

  const renderTitles = (
    collection: Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo,
    collectionIndex: number
  ) => {
    if (
      (currentScreenWidth <= getBreakpoint('ml') && collectionIndex === visiblePanelIndex) ||
      currentScreenWidth > getBreakpoint('ml')
    ) {
      return (
        <>
          {collection?.fields?.headlineText?.value !== '' && (
            <div className={themeData.classes.tabsClasses.tabHeadlineWrapper}>
              {themeName === 'aw' && (
                <SvgIcon
                  icon="orange-triangle"
                  className={themeData.classes.tabsClasses.tabHeadlineIcon}
                />
              )}
              <Headline
                fields={collection?.fields}
                classes={themeData.classes.tabsClasses.tabHeadline}
              />
            </div>
          )}
          <div ref={titlesScroller} className={themeData.classes.tabsClasses.tabTitlesContainer}>
            {currentIndex > 0 && (
              <div
                className={themeData.classes.tabsClasses.leftArrowIcon}
                onClick={() => {
                  setCurrentIndex(currentIndex - 1);
                  tabRefs.current[currentIndex - 1]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                  });
                }}
              >
                <SvgIcon icon="arrow-left" />
              </div>
            )}
            <ul className={themeData.classes.tabsClasses.tabTitlesWrapper} key={collectionIndex}>
              {collection?.fields?.children?.map(
                (
                  item: Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo,
                  itemIndex: number
                ) => {
                  return (
                    <li
                      key={itemIndex}
                      id={item.fields?.contentId.value || `tab-${normalizeGuid(item.id)}`}
                      ref={(el) => (tabRefs.current[itemIndex] = el)}
                      className={classNames(
                        currentScreenWidth >= getBreakpoint('ml')
                          ? 'break-words'
                          : 'whitespace-nowrap',
                        themeData.classes.tabsClasses.tabTitle,
                        itemIndex === currentIndex &&
                          collectionIndex === visiblePanelIndex &&
                          themeData.classes.tabsClasses.activeTabTitle
                      )}
                      onClick={() => selectTab(itemIndex, collectionIndex)}
                      onKeyDown={(e) => {
                        if (e.code === 'Enter' || e.code === 'Space') {
                          selectTab(itemIndex, collectionIndex);
                        }
                      }}
                      tabIndex={0}
                    >
                      <Text tag="h4" field={item.fields?.tabTitle as Field<string>} />
                    </li>
                  );
                }
              )}
            </ul>
            {currentIndex < collection?.fields.children?.length - 1 && (
              <div
                className={themeData.classes.tabsClasses.rightArrowIcon}
                onClick={() => {
                  setCurrentIndex(currentIndex + 1);
                  tabRefs.current[currentIndex + 1]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                  });
                }}
              >
                <SvgIcon icon="arrow-right" />
              </div>
            )}
          </div>
        </>
      );
    }

    return null;
  };

  if (!fields) {
    return null;
  }

  const renderTabTitles = () => {
    return (
      <>
        {/* Toggle Switch */}
        {children?.length > 1 && (
          <div className={themeData.classes.tabsClasses.toggleContainer}>
            {renderToggleSwitch()}
          </div>
        )}
        {/* Tab Titles List */}
        {children?.map(
          (
            collection: Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo,
            collectionIndex: number
          ) => {
            return (
              <React.Fragment key={collectionIndex}>
                {renderTitles(collection, collectionIndex)}
              </React.Fragment>
            );
          }
        )}
      </>
    );
  };

  const renderTabPanels = (): React.ReactElement[] => {
    const panels: Array<React.ReactElement> = [];
    children?.map(
      (
        collection: Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo,
        collectionIndex: number
      ) => {
        return (
          collectionIndex === visiblePanelIndex &&
          collection.fields?.children?.forEach(
            (
              tabItem: Feature.EnterpriseWeb.Enterprise.Components.Tabs.TabsFeaturedPromo.TabsFeaturedPromo,
              tabIndex: number
            ) => {
              panels.push(
                <Promo key={tabIndex} classes={themeData.classes.promoClasses} {...tabItem} />
              );
            }
          )
        );
      }
    );

    return panels;
  };

  return (
    <Component variant="lg" dataComponent="promo/tabsfeaturedpromo" {...props}>
      <div className="col-span-12">
        <Headline fields={fields} classes={themeData.classes.headline} />
      </div>
      {fields.children?.length > 0 && (
        <div className="col-span-12 mb-ml">
          <Tabs classes={themeData.classes.tabsClasses} currentTabIndex={currentIndex}>
            {renderTabTitles()}
            {renderTabPanels()}
          </Tabs>
        </div>
      )}
    </Component>
  );
};

type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      fields: ItemFieldResult[];
      children: ItemSearchResults<{
        fields: ItemFieldResult[];
      }>;
    }>;
  };
}>;

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;

  const result = {
    fields: {
      ...mapItemFieldResultsToObject(fields.data.item.fields),
      children: mapSearchResults(fields.data.item.children, (child) => {
        return {
          fields: {
            ...mapItemFieldResultsToObject(child.fields),
            children: mapSearchResults(child.children, (grandChild) => {
              return {
                fields: {
                  ...mapItemFieldResultsToObject(grandChild.fields),
                },
              };
            }),
          },
        };
      }),
    },
  };

  return result;
};

export default withDatasourceCheck()<TabsFeaturedPromoProps>(TabsFeaturedPromo);
